import { CombinedGraphQLErrors, ServerError, type ErrorLike } from '@apollo/client';
import * as Sentry from '@sentry/react';

// Codes that mean this build and the API disagree about the schema.
const REPORTED_CODES = new Set(['GRAPHQL_VALIDATION_FAILED', 'GRAPHQL_PARSE_FAILED']);

function isReportedStatus(status: number): boolean {
  return status === 400 || status >= 500;
}

function capture(error: Error, operationName: string, tags: Record<string, string | number>) {
  Sentry.withScope((scope) => {
    scope.setTag('graphql.operationName', operationName);
    scope.setTags(tags);
    scope.setFingerprint(['graphql', operationName, error.message]);
    Sentry.captureException(error);
  });
}

/** Reports the GraphQL failures that mean a broken request, not a refused one. */
export function reportGraphQLError(error: ErrorLike, operationName = 'anonymous'): void {
  if (CombinedGraphQLErrors.is(error)) {
    for (const { message, extensions } of error.errors) {
      const code = extensions?.['code'];
      if (typeof code !== 'string' || !REPORTED_CODES.has(code)) continue;
      const reported = new Error(message);
      reported.name = 'GraphQLError';
      capture(reported, operationName, { 'graphql.code': code });
    }
    return;
  }
  if (ServerError.is(error) && isReportedStatus(error.statusCode)) {
    capture(error, operationName, { 'http.status_code': error.statusCode });
  }
}
